import React, { useState, useMemo } from 'react';
import {
  View,
  StyleSheet,
  TextInput,
  FlatList,
  TouchableOpacity,
  Text,
  SectionList,
  LayoutChangeEvent,
} from 'react-native';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import { helpSections } from './data/sections';
import { DocSection } from './data/types';

import T from '../../Components/T';
import { tp } from '../../utils/translateText';
interface Section {
  category: string;
  data: DocSection[];
}

const HelpScreen: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Group sections by category and filter
  const groupedSections = useMemo(() => {
    let filtered = helpSections;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = helpSections.filter(
        (section) =>
          section.title.toLowerCase().includes(query) ||
          section.content.toLowerCase().includes(query) ||
          (section.keywords &&
            section.keywords.some((kw) => kw.toLowerCase().includes(query)))
      );
    }

    // Group by category
    const grouped = new Map<string, DocSection[]>();
    filtered.forEach((section) => {
      if (!grouped.has(section.category)) {
        grouped.set(section.category, []);
      }
      grouped.get(section.category)!.push(section);
    });

    // Convert to array, maintaining order
    const categories = [
      'Getting Started',
      'FAQ',
      'Front Office',
      'Point of Sale',
      'Store',
      'Housekeeping',
      'Asset Management',
      'Food Production',
      'Other',
    ];

    return categories
      .filter((cat) => grouped.has(cat))
      .map((category) => ({
        category,
        data: grouped.get(category)!,
      }));
  }, [searchQuery]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderInlineTokens = (text: string) => {
    const parts: Array<{ type: 'normal' | 'bold' | 'code'; content: string }> =
      [];
    let remaining = text;
    let boldRe = /\*\*([^*]+)\*\*/;
    let codeRe = /`([^`]+)`/;

    while (remaining) {
      const boldMatch = remaining.match(boldRe);
      const codeMatch = remaining.match(codeRe);

      // Find which comes first
      let nextMatch = null;
      let matchType: 'bold' | 'code' | null = null;

      if (boldMatch && codeMatch) {
        if (boldMatch.index! < codeMatch.index!) {
          nextMatch = boldMatch;
          matchType = 'bold';
        } else {
          nextMatch = codeMatch;
          matchType = 'code';
        }
      } else if (boldMatch) {
        nextMatch = boldMatch;
        matchType = 'bold';
      } else if (codeMatch) {
        nextMatch = codeMatch;
        matchType = 'code';
      }

      if (!nextMatch) {
        // No more tokens
        if (remaining) {
          parts.push({ type: 'normal', content: remaining });
        }
        break;
      }

      // Add text before token
      if (nextMatch.index! > 0) {
        parts.push({
          type: 'normal',
          content: remaining.substring(0, nextMatch.index),
        });
      }

      // Add token
      parts.push({
        type: matchType,
        content: nextMatch[1],
      });

      // Update remaining
      remaining = remaining.substring(nextMatch.index! + nextMatch[0].length);
    }

    return parts;
  };

  const renderSectionItem = ({ item }: { item: DocSection }) => {
    const isExpanded = expandedIds.has(item.id);
    const displayParts = renderInlineTokens(item.content);

    return (
      <View style={styles.itemContainer}>
        <TouchableOpacity
          style={styles.itemHeader}
          onPress={() => toggleExpanded(item.id)}
        >
          <Text style={styles.itemTitle}>{item.title}</Text>
          <FontAwesome
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            size={16}
            color="#666"
          />
        </TouchableOpacity>

        {isExpanded && (
          <View style={styles.itemContent}>
            <Text style={styles.contentText}>
              {displayParts.map((part, idx) => (
                <Text
                  key={idx}
                  style={
                    part.type === 'bold'
                      ? styles.boldText
                      : part.type === 'code'
                        ? styles.codeText
                        : undefined
                  }
                >
                  {part.content}
                </Text>
              ))}
            </Text>
          </View>
        )}
      </View>
    );
  };

  const renderSectionHeader = ({
    section,
  }: {
    section: { category: string };
  }) => (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionHeaderText}>{section.category}</Text>
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Search */}
      <View style={styles.searchContainer}>
        <FontAwesome
          name="search"
          size={16}
          color="#999"
          style={styles.searchIcon}
        />
        <TextInput
          style={styles.searchInput}
          placeholder={tp("Search help...")}
          placeholderTextColor="#999"
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity
            onPress={() => setSearchQuery('')}
            style={styles.clearButton}
          >
            <FontAwesome name="times" size={16} color="#999" />
          </TouchableOpacity>
        )}
      </View>

      {/* No results message */}
      {groupedSections.length === 0 ? (
        <View style={styles.noResults}>
          <FontAwesome name="search" size={48} color="#ccc" />
          <Text style={styles.noResultsText}><T>No help topics found</T></Text>
          <Text style={styles.noResultsSubtext}><T>Try a different search term</T></Text>
        </View>
      ) : (
        <SectionList
          sections={groupedSections}
          keyExtractor={(item) => item.id}
          renderItem={renderSectionItem}
          renderSectionHeader={renderSectionHeader}
          contentContainerStyle={styles.listContent}
          scrollEnabled={true}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#f9fafb',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  searchIcon: {
    marginRight: 10,
  },
  searchInput: {
    flex: 1,
    height: 40,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
    color: '#1f2937',
    backgroundColor: '#fff',
  },
  clearButton: {
    marginLeft: 10,
    padding: 8,
  },
  listContent: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  sectionHeader: {
    paddingHorizontal: 4,
    paddingVertical: 12,
    backgroundColor: '#f3f4f6',
    marginBottom: 4,
    marginTop: 8,
  },
  sectionHeaderText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#374151',
    textTransform: 'uppercase',
  },
  itemContainer: {
    marginVertical: 4,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    overflow: 'hidden',
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: '#f9fafb',
  },
  itemTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: '#1f2937',
  },
  itemContent: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: '#fff',
  },
  contentText: {
    fontSize: 14,
    lineHeight: 22,
    color: '#374151',
  },
  boldText: {
    fontWeight: '700',
    color: '#1f2937',
  },
  codeText: {
    fontFamily: 'Courier New',
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 4,
    color: '#7c3aed',
  },
  noResults: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  noResultsText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
  },
  noResultsSubtext: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 8,
  },
});

export default HelpScreen;
